import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { BadgeAttribution, BadgeAPI } from '../../types';
import { useAppContext } from '../../context/AppContext';
import { getBadges, assignBadge, getProjectBadges } from '../../api/Badges';
import { getBadgeSeriesTreeById, getBadgeSeriesTreeByKey } from '../../api/BadgeSeries';
import { isSoftSkillsSeries, hasTypedSavoirSavoirFaireItems } from '../../constants/badgeAxes';
import {
  CATALOG_KEY_AUDIOVISUELLE,
  CATALOG_KEY_CPS,
  isSoftSkillsCatalog,
  seriesGroupKey,
} from '../../constants/catalogSeries';
import { SOFT_SKILLS_SERIES_NAME } from '../../utils/badgeLevelLabels';
import { isSingleSelectCompetenceSeries } from '../../utils/badgeAssignmentCompetenceSelection';
import { getLevelLabel } from '../../utils/badgeLevelLabels';
import {
  axesAsOptions,
  badgeNamesForTree,
  treeCompetencesAsCards,
  treeHasAxes,
  treeHasCompetenceCards,
} from '../../utils/seriesTreeHelpers';
import { SeriesTree } from '../../types/badgeSeriesTree';
import { PublicProofPayload } from '../../types/badgeProofApi';
import { ProofData } from '../../types/proof';
import { normalizeBadgeProofResponse, mapProofApiToProofData } from '../../utils/pbProofMapper';
import { useToast } from '../../hooks/useToast';
import apiClient from '../../api/config';
import {
  getBadgeDisplayName,
  getBadgeCompetencies,
  getCompetencyDisplayName,
} from './BadgeAssignmentModal';
import { ProofCardCompact } from '../Proof/ProofCardCompact';
import { readAttestLastSeries, writeAttestLastSeries } from '../../utils/attestLastSeries';
import {
  getCpsLocalBadgeImage,
  isCompetencesPsychosocialesSeries,
} from '../../utils/cpsSeries';
import {
  cpsPastilleLabelFromBadgeName,
  formatBadgeHeadingWithCode,
  phaseNumberFromCpsCompetenceCode,
  sortBadgeNamesBySpfCode,
} from '../../utils/cpsPastilleLabels';
import './Modal.css';
import './BadgeAssignmentModal.css';

function mapAssignProofToProofData(proof: PublicProofPayload): ProofData {
  const normalized = normalizeBadgeProofResponse(
    proof as unknown as Record<string, unknown>,
    ''
  );
  const data = mapProofApiToProofData(normalized);
  // Assign payload has no share_token — clear share affordances
  return {
    ...data,
    shareToken: '',
    shareUrl: '',
    showRightsLink: false,
  };
}

function proofsFromAssignResponse(response: {
  assignments?: Array<{ proof?: PublicProofPayload }>;
}): ProofData[] {
  return (response.assignments || [])
    .map((a) => a.proof)
    .filter((p): p is PublicProofPayload => Boolean(p))
    .map(mapAssignProofToProofData);
}

interface AttestCompetenceModalProps {
  onClose: () => void;
  onAssign: (badgeData: BadgeAttribution) => void;
  participants: {
    id: string;
    memberId: string;
    name: string;
    avatar: string;
    organization?: string;
  }[];
  preselectedParticipant?: string | null;
  projectId?: string;
  projectTitle?: string;
  availableOrganizations?: Array<{
    id: number;
    name: string;
    type: 'School' | 'Company';
    role?: string;
  }>;
}

type CompetenceDraft = {
  name: string;
  level: string;
  expanded: boolean;
  expertiseIds: number[];
  comment: string;
  file: File | null;
  fileName: string;
};

type FrozenProof = {
  name: string;
  level: string;
  date?: string;
};

// Constat ajusté individuellement (mode « participant par participant », écran 8).
type ParticipantConstat = {
  expertiseIds: number[];
  comment: string;
  file: File | null;
  fileName: string;
};

const CATALOGUE_MARKERS = [
  'digcomp',
  'psychosocial',
  'toukouleur',
  'parcours des possibles',
  'audiovisuelle',
  'parcours professionnel',
  'métiers de la mer',
  "s'orienter",
  'soft skills',
];

const PARTICIPANTS_INITIAL_COUNT = 8;

const chipName = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
};

// « Léa M., Amir B. et Chloé D. » — recap groupé du constat (écran 5, mode groupe).
const joinFr = (names: string[]): string => {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(', ')} et ${names[names.length - 1]}`;
};

const seriesProvenance = (sample?: BadgeAPI | null): 'own' | 'catalogue' => {
  if (!sample) return 'own';
  if (sample.catalog_key) return 'catalogue';
  const n = (sample.series || '').toLowerCase();
  return CATALOGUE_MARKERS.some((m) => n.includes(m)) ? 'catalogue' : 'own';
};

const commentRequiredFor = (badge: BadgeAPI): boolean => {
  const isAudiovisuelle = badge.catalog_key === CATALOG_KEY_AUDIOVISUELLE;
  if (isAudiovisuelle && (badge.level === 'level_3' || badge.level === 'level_4')) return true;
  if (isSoftSkillsCatalog({ catalog_key: badge.catalog_key }) && badge.level === 'level_3') {
    return true;
  }
  return badge.level === 'level_2';
};

const documentRequiredFor = (badge: BadgeAPI): boolean => {
  const isAudiovisuelle = badge.catalog_key === CATALOG_KEY_AUDIOVISUELLE;
  if (isAudiovisuelle && (badge.level === 'level_3' || badge.level === 'level_4')) return true;
  if (isSoftSkillsCatalog({ catalog_key: badge.catalog_key }) && badge.level === 'level_3') {
    return true;
  }
  return false;
};

type ChecklistItem = { id: number; name: string; category?: 'domain' | 'expertise' | 'both' };

// Liste des items de constat, partagée par les 3 écrans où elle apparaît (étape
// Compétence, étape Constat en mode groupe, étape Constat en mode participant par
// participant). Rendu à plat pour la plupart des séries ; groupé en deux bandeaux
// « Je connais — savoirs » / « Je fais — savoir-faire » pour les séries qui déclarent
// ce typage (CPS aujourd'hui — hasTypedSavoirSavoirFaireItems, P2.13 V1.2 écran 5/14).
// L'item à double type (category: both, ex. CPS S2.1) est affiché une seule fois, dans
// sa propre section — la case mi-verte mi-violette (V1.2, note écran 5) reste à faire.
const ChecklistItems: React.FC<{
  comps: ChecklistItem[];
  checkedIds: number[];
  series: string;
  onToggle: (id: number) => void;
  onToggleAll: () => void;
  hint?: string;
}> = ({ comps, checkedIds, series, onToggle, onToggleAll, hint }) => {
  if (comps.length === 0) return null;

  const renderItem = (c: ChecklistItem) => {
    const on = checkedIds.includes(c.id);
    const typeClass = c.category === 'domain' ? 'gr' : c.category === 'expertise' ? 'vi' : '';
    return (
      <button key={c.id} type="button" className={`att-subline ${on ? 'on' : ''}`} onClick={() => onToggle(c.id)}>
        <span className={`att-box ${on ? 'on' : ''} ${typeClass}`} />
        <span>{getCompetencyDisplayName(c.name, false)}</span>
      </button>
    );
  };

  const typed = hasTypedSavoirSavoirFaireItems(series) && comps.some((c) => c.category);
  const savoirOnly = comps.filter((c) => c.category === 'domain');
  const savoirFaireOnly = comps.filter((c) => c.category === 'expertise');
  const both = comps.filter((c) => c.category === 'both');

  return (
    <>
      <div className="att-subs-title">
        <span>
          {checkedIds.length} sur {comps.length}
        </span>
        {comps.length > 1 && (
          <button type="button" className="att-tout-cocher" onClick={onToggleAll}>
            {checkedIds.length === comps.length ? 'Tout décocher' : 'Tout cocher'}
          </button>
        )}
      </div>
      {typed ? (
        <>
          {savoirOnly.length > 0 && (
            <>
              <div className="att-type-label gr">Je connais — savoirs</div>
              {savoirOnly.map(renderItem)}
            </>
          )}
          {savoirFaireOnly.length > 0 && (
            <>
              <div className="att-type-label vi">Je fais — savoir-faire</div>
              {savoirFaireOnly.map(renderItem)}
            </>
          )}
          {both.length > 0 && (
            <>
              <div className="att-type-label both">Je connais et je fais — savoir et savoir-faire</div>
              {both.map(renderItem)}
            </>
          )}
        </>
      ) : (
        comps.map(renderItem)
      )}
      <div className="att-hint" style={{ marginTop: 4 }}>
        {hint || 'Un seul item coché suffit à produire une preuve.'}
      </div>
    </>
  );
};

const AttestCompetenceModal: React.FC<AttestCompetenceModalProps> = ({
  onClose,
  onAssign,
  participants,
  preselectedParticipant,
  projectId,
  projectTitle,
  availableOrganizations,
}) => {
  const { state } = useAppContext();
  const { showWarning: showWarningToast, showError: showErrorToast, showSuccess: showSuccessToast } = useToast();
  const isIndividual = Boolean(preselectedParticipant);

  const [series, setSeries] = useState('');
  const [seriesQuery, setSeriesQuery] = useState('');
  const [showAllCompetences, setShowAllCompetences] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, CompetenceDraft>>({});
  const [selectedParticipants, setSelectedParticipants] = useState<string[]>(
    preselectedParticipant ? [preselectedParticipant] : []
  );
  const [participantsSearchTerm, setParticipantsSearchTerm] = useState('');
  const [participantsExpanded, setParticipantsExpanded] = useState(false);
  const [commentaire, setCommentaire] = useState('');
  const [fichier, setFichier] = useState<File | null>(null);
  const [fileName, setFileName] = useState('');
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<number | undefined>(undefined);
  const [selon, setSelon] = useState<'project' | 'event'>('project');
  const [eventId, setEventId] = useState<number | undefined>(undefined);
  const [eventOpen, setEventOpen] = useState(false);
  const [commentFieldOpen, setCommentFieldOpen] = useState(false);
  const [proofFieldOpen, setProofFieldOpen] = useState(false);
  const [projectEvents, setProjectEvents] = useState<Array<{ id: number; title: string; date?: string }>>([]);
  const [frozen, setFrozen] = useState<FrozenProof[]>([]);
  const [badges, setBadges] = useState<BadgeAPI[]>([]);
  const [loadingBadges, setLoadingBadges] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  /** P2.13 V1.4 — first click arms, second click submits (no confirm overlay). */
  const [attestArmed, setAttestArmed] = useState(false);
  const [successState, setSuccessState] = useState<{
    count: number;
    badgeName: string;
    proofs: ProofData[];
    participantNames: string[];
    itemLabels: string[];
    observedAtLabel: string;
  } | null>(null);
  const [successShowAllProofs, setSuccessShowAllProofs] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  /** User forced the série screen (Retour after last-series skip). */
  const [serieScreenForced, setSerieScreenForced] = useState(false);
  const [lastSeriesTag, setLastSeriesTag] = useState<string | null>(null);
  const [constatMode, setConstatMode] = useState<'groupe' | 'individuel'>('groupe');
  const [individualDrafts, setIndividualDrafts] = useState<Record<string, ParticipantConstat>>({});
  const [activeParticipantId, setActiveParticipantId] = useState<string | null>(null);
  /** F2 catalogue tree for Écran 3 (axes → compétences → badges) */
  const [seriesTree, setSeriesTree] = useState<SeriesTree | null>(null);
  const [treeLoading, setTreeLoading] = useState(false);

  const displaySeries = useCallback((seriesName: string) => {
    return isSoftSkillsSeries(seriesName) ? SOFT_SKILLS_SERIES_NAME : seriesName;
  }, []);

  useEffect(() => {
    if (preselectedParticipant) setSelectedParticipants([preselectedParticipant]);
  }, [preselectedParticipant]);

  useEffect(() => {
    const fetchBadges = async () => {
      setLoadingBadges(true);
      try {
        setBadges(await getBadges());
      } catch {
        showErrorToast('Erreur lors du chargement des compétences');
      } finally {
        setLoadingBadges(false);
      }
    };
    void fetchBadges();
    // Fetch once on open — toast helpers are a new reference every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!projectId) return;
    void apiClient
      .get(`/api/v1/projects/${projectId}/events`)
      .then((res) => {
        const list = res.data?.data ?? res.data ?? [];
        setProjectEvents(Array.isArray(list) ? list : []);
      })
      .catch(() => setProjectEvents([]));
  }, [projectId]);

  useEffect(() => {
    if (!isIndividual || !projectId || !preselectedParticipant) return;
    void getProjectBadges(Number(projectId), 1, 50, { receiver_query: undefined })
      .then((res) => {
        const rows = (res.data || [])
          .filter((item: any) => String(item.receiver?.id || item.receiver_id) === String(preselectedParticipant))
          .map((item: any) => {
            const badge = item.badge || {};
            const date = item.assigned_at || item.created_at;
            return {
              name: badge.name || 'Compétence',
              level: badge.level ? String(badge.level).replace('level_', '') : '1',
              date: date ? new Date(date).toLocaleDateString('fr-FR') : undefined,
            } as FrozenProof;
          });
        setFrozen(rows);
      })
      .catch(() => setFrozen([]));
  }, [isIndividual, projectId, preselectedParticipant]);

  const organizationsForSelection = useMemo(() => {
    if (availableOrganizations && availableOrganizations.length > 0) return availableOrganizations;
    const contexts = state.user?.available_contexts;
    const orgs: Array<{ id: number; name: string; type: 'School' | 'Company'; role?: string }> = [];
    const badgeRoles = ['superadmin', 'admin', 'referent', 'référent', 'intervenant'];
    contexts?.schools?.forEach((school: any) => {
      if (badgeRoles.includes(school.role?.toLowerCase() || '')) {
        orgs.push({ id: school.id, name: school.name || 'École', type: 'School', role: school.role });
      }
    });
    contexts?.companies?.forEach((company: any) => {
      if (badgeRoles.includes(company.role?.toLowerCase() || '')) {
        orgs.push({ id: company.id, name: company.name || 'Organisation', type: 'Company', role: company.role });
      }
    });
    return orgs;
  }, [availableOrganizations, state.user?.available_contexts]);

  useEffect(() => {
    if (organizationsForSelection.length === 1) {
      setSelectedOrganizationId(organizationsForSelection[0].id);
    }
  }, [organizationsForSelection]);

  const badgesBySeries = useMemo(() => {
    const organized: { [seriesKey: string]: BadgeAPI[] } = {};
    badges.forEach((badge) => {
      const key = seriesGroupKey(badge);
      if (!organized[key]) organized[key] = [];
      organized[key].push(badge);
    });
    return organized;
  }, [badges]);

  const availableSeries = useMemo(() => Object.keys(badgesBySeries), [badgesBySeries]);

  /** Patrick: attester never appears in the participant list. */
  const attestParticipants = useMemo(() => {
    const uid = state.user?.id != null ? String(state.user.id) : null;
    if (!uid) return participants;
    return participants.filter(
      (p) => String(p.memberId) !== uid && String(p.id) !== uid
    );
  }, [participants, state.user?.id]);

  // F2 — load catalogue tree by badge_series_id or catalog_key from selected series badges (R1 / 01/10)
  useEffect(() => {
    if (!series) {
      setSeriesTree(null);
      return;
    }
    const sample = badgesBySeries[series]?.[0];
    const seriesId = sample?.badge_series_id ?? null;
    const catalogKey = sample?.catalog_key ?? null;
    if (seriesId == null && !catalogKey) {
      setSeriesTree(null);
      return;
    }
    let cancelled = false;
    setTreeLoading(true);
    const fetchTree =
      seriesId != null
        ? getBadgeSeriesTreeById(seriesId)
        : getBadgeSeriesTreeByKey(catalogKey as string);
    void fetchTree
      .then((tree) => {
        if (!cancelled) setSeriesTree(tree);
      })
      .catch(() => {
        if (!cancelled) setSeriesTree(null);
      })
      .finally(() => {
        if (!cancelled) setTreeLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [series, badgesBySeries]);

  /**
   * P2.13 V1.4.1 — no dedicated « Axe » step: all series competences on one screen,
   * axes as section titles + optional filter chips (not a wizard step).
   */
  const wizardHasAxeStep = false;

  const treeAxeOptions = useMemo(() => {
    if (!seriesTree || !treeHasAxes(seriesTree)) return [];
    const opts = axesAsOptions(seriesTree);
    return opts.some((a) => a.badgeNames.length > 0) ? opts : [];
  }, [seriesTree]);

  /** Patrick P2.13 : étape 2 toujours « Compétence » (toutes séries). */
  const leafLabel = 'Compétence';

  const seriesLabel = (seriesKey: string): string => {
    const sample = badgesBySeries[seriesKey]?.[0];
    if (!sample) return seriesKey;
    return displaySeries(sample.series);
  };

  const groupedSeries = useMemo(() => {
    const q = seriesQuery.trim().toLowerCase();
    const list = availableSeries.filter((s) => {
      const label = seriesLabel(s).toLowerCase();
      return !q || label.includes(q) || s.toLowerCase().includes(q);
    });
    return {
      own: list.filter((s) => seriesProvenance(badgesBySeries[s]?.[0]) === 'own'),
      catalogue: list.filter((s) => seriesProvenance(badgesBySeries[s]?.[0]) === 'catalogue'),
    };
  }, [availableSeries, seriesQuery, badgesBySeries]);

  const seriesBadges = useMemo(() => {
    return (badgesBySeries[series] || []).filter((b) => b.name !== 'Test Badge');
  }, [badgesBySeries, series]);

  const competenceNames = useMemo(() => {
    const names: string[] = [];
    seriesBadges.forEach((b) => {
      if (!names.includes(b.name)) names.push(b.name);
    });
    return names;
  }, [seriesBadges]);

  const frozenNames = useMemo(() => new Set(frozen.map((f) => f.name)), [frozen]);

  const visibleNames = useMemo(() => {
    const rest = competenceNames.filter((n) => !frozenNames.has(n));
    if (showAllCompetences || rest.length <= 4) return rest;
    const selected = rest.filter((n) => drafts[n]);
    const others = rest.filter((n) => !drafts[n]);
    return [...selected, ...others].slice(0, Math.max(4, selected.length));
  }, [competenceNames, frozenNames, showAllCompetences, drafts]);

  const levelsFor = useCallback(
    (name: string): string[] => {
      const levels = seriesBadges
        .filter((b) => b.name === name)
        .map((b) => b.level.replace('level_', ''))
        .filter((v, i, a) => a.indexOf(v) === i)
        .sort();
      const base = levels.length ? levels : ['1'];
      // CPS: only Phase 1 and 2 — detect via catalog_key on series badges (R1 / 01/10)
      if (seriesBadges.some((b) => b.catalog_key === CATALOG_KEY_CPS)) {
        return base.filter((lv) => lv === '1' || lv === '2');
      }
      return base;
    },
    [seriesBadges]
  );

  const badgeFor = useCallback(
    (name: string, level: string): BadgeAPI | undefined =>
      seriesBadges.find((b) => b.name === name && b.level === `level_${level}`),
    [seriesBadges]
  );

  /** Resolve catalog identity for level labels (avoid id:N / key: falling through to Soft Skills). */
  const levelLabelFor = (level: string, competenceName?: string): string => {
    const sample =
      (competenceName &&
        (badgeFor(competenceName, level) ||
          seriesBadges.find((b) => b.name === competenceName))) ||
      seriesBadges[0];
    return getLevelLabel(sample?.catalog_key || sample?.series || series, level);
  };

  const competenceRecap = (draft: CompetenceDraft, badge: BadgeAPI | undefined) => {
    if (!badge) return null;
    const comps = getBadgeCompetencies(badge);
    if (comps.length === 0) return null;
    // V1.1 (décision du 17/09) : la règle de complétion est retirée — un seul
    // item coché suffit, la même règle pour toutes les séries.
    return {
      checked: draft.expertiseIds.length,
      total: comps.length,
      comps,
    };
  };

  // V1.1 : un geste = une compétence observée à la fois — la sélection de la
  // compétence (avec son niveau) se fait à l'étape « Compétence » du tunnel
  // (voir selectCompetence plus bas, écran Compétence) ; ici on garde la
  // validation, le "tout cocher" et le fil des items (partagés entre écrans).
  const toggleExpertise = (name: string, expertiseId: number, single: boolean) => {
    setDrafts((prev) => {
      const cur = prev[name];
      if (!cur) return prev;
      const nextIds = single
        ? [expertiseId]
        : cur.expertiseIds.includes(expertiseId)
          ? cur.expertiseIds.filter((id) => id !== expertiseId)
          : [...cur.expertiseIds, expertiseId];
      return { ...prev, [name]: { ...cur, expertiseIds: nextIds, expanded: true } };
    });
  };

  // « Tout cocher », avec rappel du nombre à gauche — présent sur tous les
  // écrans de constat, quelle que soit la longueur de la liste (V1.1, écran 5).
  const toggleAllExpertise = (name: string, allIds: number[], currentIds: number[]) => {
    setDrafts((prev) => {
      const cur = prev[name];
      if (!cur) return prev;
      const nextIds = currentIds.length === allIds.length ? [] : allIds;
      return { ...prev, [name]: { ...cur, expertiseIds: nextIds, expanded: true } };
    });
  };

  // Ajuster participant par participant (écran 8) : même logique de case à
  // cocher que le mode groupe, mais un jeu d'items propre à chaque personne.
  const toggleIndividualExpertise = (participantId: string, expertiseId: number, single: boolean) => {
    setIndividualDrafts((prev) => {
      const cur = prev[participantId];
      if (!cur) return prev;
      const nextIds = single
        ? [expertiseId]
        : cur.expertiseIds.includes(expertiseId)
          ? cur.expertiseIds.filter((id) => id !== expertiseId)
          : [...cur.expertiseIds, expertiseId];
      return { ...prev, [participantId]: { ...cur, expertiseIds: nextIds } };
    });
  };

  const toggleAllIndividualExpertise = (participantId: string, allIds: number[], currentIds: number[]) => {
    setIndividualDrafts((prev) => {
      const cur = prev[participantId];
      if (!cur) return prev;
      const nextIds = currentIds.length === allIds.length ? [] : allIds;
      return { ...prev, [participantId]: { ...cur, expertiseIds: nextIds } };
    });
  };


  // V1.1 (décision du 17/09) : toute règle de complétion est retirée. Chaque
  // item constaté produit sa preuve, quel qu'il soit et quels que soient les
  // autres — la même règle pour toutes les séries : il suffit d'en cocher un.
  const validateLocal = (
    selectedExpertiseIds: number[],
    _badge: BadgeAPI,
    _allExpertises: Array<{ id: number; name: string }>
  ) => {
    return selectedExpertiseIds.length >= 1
      ? { isValid: true, errorMessage: null }
      : { isValid: false, errorMessage: 'Cochez au moins un item constaté.' };
  };

  const filteredParticipants = useMemo(() => {
    const term = participantsSearchTerm.trim().toLowerCase();
    if (!term) return attestParticipants;
    return attestParticipants.filter(
      (p) =>
        (p.name || '').toLowerCase().includes(term) ||
        (p.organization || '').toLowerCase().includes(term)
    );
  }, [attestParticipants, participantsSearchTerm]);

  const toggleParticipant = (memberId: string) => {
    if (isIndividual) return;
    setSelectedParticipants((prev) =>
      prev.includes(memberId) ? prev.filter((id) => id !== memberId) : [...prev, memberId]
    );
    setConstatMode('groupe');
    setIndividualDrafts({});
    setActiveParticipantId(null);
  };

  const selectAllParticipants = () => {
    setSelectedParticipants(filteredParticipants.map((p) => p.memberId));
    setConstatMode('groupe');
    setIndividualDrafts({});
    setActiveParticipantId(null);
  };

  // ---- Assistant V1.1 : un ecran par etape, un clic sur une ligne avance ----
  type WizardStepKey = 'serie' | 'axe' | 'competence' | 'participants' | 'constat';

  const stepDefs: { key: WizardStepKey; label: string }[] = useMemo(() => {
    const base: { key: WizardStepKey; label: string }[] = [
      { key: 'serie', label: 'Série' },
      { key: 'axe', label: 'Axe' },
      { key: 'competence', label: leafLabel },
      { key: 'participants', label: 'Participants' },
      { key: 'constat', label: 'Constat' },
    ];
    const withAxes = wizardHasAxeStep ? base : base.filter((s) => s.key !== 'axe');
    return isIndividual ? withAxes.filter((s) => s.key !== 'participants') : withAxes;
  }, [isIndividual, wizardHasAxeStep, leafLabel]);

  // Une seule série disponible : cet écran n'existe pas (V1.1, écran 1).
  const hasSerieScreen = availableSeries.length !== 1;
  const firstStep: WizardStepKey = hasSerieScreen ? 'serie' : wizardHasAxeStep ? 'axe' : 'competence';

  const [step, setStep] = useState<WizardStepKey>(firstStep);
  const [selectedAxe, setSelectedAxe] = useState<string | null>(null);

  useEffect(() => {
    if (!hasSerieScreen && series && step === 'serie') {
      setStep(wizardHasAxeStep ? 'axe' : 'competence');
    }
  }, [hasSerieScreen, series, step, wizardHasAxeStep]);

  useEffect(() => {
    if (loadingBadges || availableSeries.length === 0) return;
    if (series) return;

    if (availableSeries.length === 1) {
      setSeries(availableSeries[0]);
      setStep('competence');
      return;
    }

    if (serieScreenForced) return;

    const saved = readAttestLastSeries(state.user?.id, projectId);
    if (saved?.series && availableSeries.includes(saved.series)) {
      setLastSeriesTag(saved.series);
      setSeries(saved.series);
      setStep('competence');
    }
  }, [
    loadingBadges,
    availableSeries,
    series,
    serieScreenForced,
    state.user?.id,
    projectId,
  ]);

  useEffect(() => {
    setAttestArmed(false);
  }, [step, drafts, selectedParticipants, constatMode, individualDrafts]);

  const currentStepIndex = stepDefs.findIndex((s) => s.key === step);

  // Écran 11 : sortie en cours de liste. On ne redemande rien tant que rien
  // n'a été engagé (premier écran) ni après une attestation déjà produite.
  const hasProgress = !successState && currentStepIndex > 0;
  const requestClose = () => {
    if (hasProgress) {
      setCloseConfirmOpen(true);
      return;
    }
    onClose();
  };

  const chooseSeries = (s: string) => {
    setSeries(s);
    setDrafts({});
    setSelectedAxe(null);
    setShowAllCompetences(false);
    setSeriesQuery('');
    setConstatMode('groupe');
    setIndividualDrafts({});
    setActiveParticipantId(null);
    setSeriesTree(null);
    setSerieScreenForced(false);
    setStep('competence');
  };

  const selectCompetence = (name: string, level: string) => {
    setDrafts((prev) => {
      if (prev[name] && prev[name].level === level) return prev;
      const badge = badgeFor(name, level);
      const comps = badge ? getBadgeCompetencies(badge) : [];
      return {
        [name]: {
          name,
          level,
          expanded: comps.length > 0,
          expertiseIds: [],
          comment: '',
          file: null,
          fileName: '',
        },
      };
    });
    setConstatMode('groupe');
    setIndividualDrafts({});
    setActiveParticipantId(null);
  };

  /** P2.13 — pastille click selects competence+level and advances. */
  const pickCompetenceAndAdvance = (name: string, level: string) => {
    selectCompetence(name, level);
    setStep(isIndividual ? 'constat' : 'participants');
  };

  const goBack = () => {
    if (step === 'axe') {
      setSerieScreenForced(true);
      setStep('serie');
      return;
    }
    if (step === 'competence') {
      setSerieScreenForced(true);
      setStep(wizardHasAxeStep ? 'axe' : 'serie');
      return;
    }
    if (step === 'participants') {
      setStep('competence');
      return;
    }
    if (step === 'constat') {
      setConstatMode('groupe');
      setIndividualDrafts({});
      setActiveParticipantId(null);
      setStep(isIndividual ? 'competence' : 'participants');
      return;
    }
  };

  const selectedOrgName = useMemo(() => {
    if (selectedOrganizationId) {
      return organizationsForSelection.find((o) => o.id === selectedOrganizationId)?.name;
    }
    return organizationsForSelection[0]?.name;
  }, [organizationsForSelection, selectedOrganizationId]);

  const selectedDraftList = Object.values(drafts);
  const pickedName = selectedDraftList[0]?.name;
  const pickedDraft = pickedName ? drafts[pickedName] : undefined;
  const pickedBadge = pickedDraft ? badgeFor(pickedDraft.name, pickedDraft.level) : undefined;
  const pickedComps = pickedBadge ? getBadgeCompetencies(pickedBadge) : [];

  /** Patrick reprise 4 — Attester inactif tant qu’aucun item n’est coché (s’il y a des items). */
  const canAttest = useMemo(() => {
    if (pickedComps.length === 0) return true;
    if (!isIndividual && constatMode === 'individuel') {
      return selectedParticipants.every(
        (pid) => (individualDrafts[pid]?.expertiseIds.length ?? 0) >= 1
      );
    }
    return (pickedDraft?.expertiseIds.length ?? 0) >= 1;
  }, [
    pickedComps.length,
    isIndividual,
    constatMode,
    selectedParticipants,
    individualDrafts,
    pickedDraft?.expertiseIds.length,
  ]);

  const selectedBadges = selectedDraftList
    .map((d) => badgeFor(d.name, d.level))
    .filter((b): b is BadgeAPI => Boolean(b));

  const massNeedsComment = !isIndividual && selectedBadges.some(commentRequiredFor);
  const massNeedsDoc = !isIndividual && selectedBadges.some(documentRequiredFor);
  const massRecommendsDoc = !isIndividual && selectedBadges.some((b) => b.level === 'level_2');

  const proofCount = selectedParticipants.length * selectedDraftList.length;
  const selectedParticipantNames = selectedParticipants.map((id) => {
    const p = participants.find((pp) => pp.memberId === id);
    return p ? chipName(p.name) : '';
  }).filter(Boolean);
  const preselectedPerson = participants.find((p) => p.memberId === preselectedParticipant);

  // Écran 8 : « Ajuster participant par participant ». Le groupe reste la
  // valeur par défaut ; ce lien fait bifurquer vers une séquence individuelle,
  // amorcée avec ce qui était coché en commun (rien n'est perdu au basculement).
  const enterIndividualMode = () => {
    if (!pickedDraft) return;
    const base: ParticipantConstat = {
      expertiseIds: pickedDraft.expertiseIds,
      comment: commentaire,
      file: fichier,
      fileName,
    };
    const next: Record<string, ParticipantConstat> = {};
    selectedParticipants.forEach((id) => {
      next[id] = { ...base };
    });
    setIndividualDrafts(next);
    setActiveParticipantId(selectedParticipants[0] || null);
    setConstatMode('individuel');
  };

  const exitIndividualMode = () => {
    setConstatMode('groupe');
    setIndividualDrafts({});
    setActiveParticipantId(null);
  };

  // Écran 7 : enchaînement après succès. On repart du même projet/organisation,
  // sans tout refermer -- deux raccourcis courants plutôt qu'un retour à zéro.
  const resetConstatExtras = () => {
    setConstatMode('groupe');
    setIndividualDrafts({});
    setActiveParticipantId(null);
    setCommentaire('');
    setFichier(null);
    setFileName('');
    setCommentFieldOpen(false);
    setProofFieldOpen(false);
    setSelon('project');
    setEventId(undefined);
    setEventOpen(false);
  };

  const handleOtherCompetence = () => {
    setSuccessState(null);
    setSuccessShowAllProofs(false);
    setDrafts({});
    resetConstatExtras();
    setStep(wizardHasAxeStep ? 'axe' : 'competence');
  };

  const handleSameCompetenceOtherParticipants = () => {
    setSuccessState(null);
    setSuccessShowAllProofs(false);
    setSelectedParticipants([]);
    setParticipantsSearchTerm('');
    setParticipantsExpanded(false);
    resetConstatExtras();
    setStep('participants');
  };

  const namesForCompetenceStep = useMemo(() => {
    const unfrozen = competenceNames.filter((n) => !frozenNames.has(n));
    if (selectedAxe) {
      const fromTree = treeAxeOptions.find((a) => a.title === selectedAxe);
      if (fromTree) {
        return unfrozen.filter((n) => fromTree.badgeNames.includes(n));
      }
    }
    if (treeAxeOptions.length > 0) {
      // Full series on one screen (axes as titles) — no 4-item truncate.
      return unfrozen;
    }
    if (seriesTree && !treeHasAxes(seriesTree)) {
      const fromRoot = badgeNamesForTree(seriesTree);
      if (fromRoot.length > 0) {
        return unfrozen.filter((n) => fromRoot.includes(n));
      }
    }
    return visibleNames;
  }, [
    selectedAxe,
    treeAxeOptions,
    competenceNames,
    frozenNames,
    visibleNames,
    seriesTree,
  ]);

  /** Competence names grouped under axe titles (flat fallback — soft skills etc.). */
  const competenceGroups = useMemo(() => {
    if (treeAxeOptions.length === 0 || selectedAxe) {
      return [{ title: selectedAxe || null, names: namesForCompetenceStep }];
    }
    const used = new Set<string>();
    const groups: { title: string | null; names: string[] }[] = [];
    treeAxeOptions.forEach((axe) => {
      const names = namesForCompetenceStep.filter((n) => axe.badgeNames.includes(n));
      if (names.length === 0) return;
      names.forEach((n) => used.add(n));
      groups.push({ title: axe.title, names });
    });
    const orphan = namesForCompetenceStep.filter((n) => !used.has(n));
    if (orphan.length > 0) groups.push({ title: null, names: orphan });
    return groups;
  }, [treeAxeOptions, selectedAxe, namesForCompetenceStep]);

  /**
   * P2.13 reprise 1 — parent competence cards (C1…) with CPS-spécifique pastilles.
   * Empty → fall back to flat competenceGroups (one card per badge name).
   */
  const parentCompetenceCards = useMemo(() => {
    if (!treeHasCompetenceCards(seriesTree)) return [];
    const nameSet = new Set(namesForCompetenceStep);
    return treeCompetencesAsCards(seriesTree)
      .map((card) => ({
        ...card,
        badgeNames: card.badgeNames.filter((n) => nameSet.has(n)),
      }))
      .filter((card) => {
        if (card.badgeNames.length === 0) return false;
        if (!selectedAxe) return true;
        return card.axeTitle === selectedAxe;
      });
  }, [seriesTree, namesForCompetenceStep, selectedAxe]);

  const useParentCompetenceCards = parentCompetenceCards.length > 0;

  const handleSubmit = async () => {
    if (selectedDraftList.length === 0) {
      showWarningToast('Cochez au moins une compétence');
      return;
    }
    if (selectedParticipants.length === 0) {
      showWarningToast('Veuillez sélectionner au moins un participant');
      return;
    }
    if (!projectId) {
      showErrorToast('ID du projet manquant');
      return;
    }
    if (organizationsForSelection.length > 1 && !selectedOrganizationId) {
      showWarningToast('Veuillez sélectionner une organisation');
      return;
    }

    const useIndividualConstats = !isIndividual && constatMode === 'individuel';

    for (const draft of selectedDraftList) {
      const badge = badgeFor(draft.name, draft.level);
      if (!badge) {
        showWarningToast(`Niveau indisponible pour « ${getBadgeDisplayName(draft.name)} »`);
        return;
      }
      const comps = getBadgeCompetencies(badge);

      if (useIndividualConstats) {
        for (const pid of selectedParticipants) {
          const pd = individualDrafts[pid];
          const pLabel = (() => {
            const p = participants.find((x) => x.memberId === pid);
            return p ? chipName(p.name) : 'ce participant';
          })();
          if (comps.length > 0) {
            const validation = validateLocal(pd?.expertiseIds || [], badge, comps);
            if (!validation.isValid && validation.errorMessage) {
              showWarningToast(`${pLabel} — ${getBadgeDisplayName(draft.name)} : ${validation.errorMessage}`);
              return;
            }
          }
          if (documentRequiredFor(badge) && !pd?.file) {
            showWarningToast(`Joignez un document pour ${pLabel}`);
            return;
          }
          if (commentRequiredFor(badge)) {
            const text = (pd?.comment || '').trim();
            if (!text) {
              showWarningToast(`Le commentaire est obligatoire pour ${pLabel}`);
              return;
            }
            if (
              isSoftSkillsCatalog({ catalog_key: badge.catalog_key, series: badge.series }) &&
              badge.level === 'level_3' &&
              text.length < 100
            ) {
              showWarningToast(`Le commentaire doit contenir au moins 100 caractères pour le niveau 3 de la série ${SOFT_SKILLS_SERIES_NAME}`);
              return;
            }
          }
        }
        continue;
      }

      if (comps.length > 0) {
        const validation = validateLocal(draft.expertiseIds, badge, comps);
        if (!validation.isValid && validation.errorMessage) {
          showWarningToast(`${getBadgeDisplayName(draft.name)} : ${validation.errorMessage}`);
          return;
        }
      }
      if (documentRequiredFor(badge)) {
        const file = isIndividual ? draft.file : fichier;
        if (!file) {
          showWarningToast(`Joignez un document pour ${getBadgeDisplayName(draft.name)}`);
          return;
        }
      }
      if (commentRequiredFor(badge)) {
        const text = (isIndividual ? draft.comment : commentaire).trim();
        if (!text) {
          showWarningToast(`Le commentaire est obligatoire pour ${getBadgeDisplayName(draft.name)}`);
          return;
        }
        if (
          isSoftSkillsCatalog({ catalog_key: badge.catalog_key, series: badge.series }) &&
          badge.level === 'level_3' &&
          text.length < 100
        ) {
          showWarningToast(`Le commentaire doit contenir au moins 100 caractères pour le niveau 3 de la série ${SOFT_SKILLS_SERIES_NAME}`);
          return;
        }
      }
    }

    const recipientIds = selectedParticipants
      .map((id) => {
        const p = participants.find((x) => x.memberId === id);
        return p ? parseInt(p.memberId, 10) : null;
      })
      .filter((id): id is number => id !== null);

    setSubmitting(true);
    try {
      let assigned = 0;
      let lastBadge: BadgeAPI | null = null;
      const collectedProofs: ProofData[] = [];

      if (useIndividualConstats) {
        for (const draft of selectedDraftList) {
          const badge = badgeFor(draft.name, draft.level);
          if (!badge) continue;
          lastBadge = badge;
          for (const pid of selectedParticipants) {
            const pd = individualDrafts[pid];
            const p = participants.find((x) => x.memberId === pid);
            if (!p) continue;
            const validExpertiseIds = (pd?.expertiseIds || []).filter((id) => id > 0);
            const response = await assignBadge(
              parseInt(projectId, 10),
              {
                badge_id: badge.id,
                recipient_ids: [parseInt(p.memberId, 10)],
                badge_skill_ids: validExpertiseIds.length > 0 ? validExpertiseIds : undefined,
                comment: pd?.comment || undefined,
                organization_id: selectedOrganizationId,
                event_id: selon === 'event' ? eventId : undefined,
              },
              pd?.file ? [pd.file] : undefined
            );
            assigned += response.assigned_count || 1;
            collectedProofs.push(...proofsFromAssignResponse(response));
          }
        }
      } else {
        for (const draft of selectedDraftList) {
          const badge = badgeFor(draft.name, draft.level);
          if (!badge) continue;
          lastBadge = badge;
          const validExpertiseIds = draft.expertiseIds.filter((id) => id > 0);
          const comment = isIndividual ? draft.comment : commentaire;
          const file = isIndividual ? draft.file : fichier;
          const response = await assignBadge(
            parseInt(projectId, 10),
            {
              badge_id: badge.id,
              recipient_ids: recipientIds,
              badge_skill_ids: validExpertiseIds.length > 0 ? validExpertiseIds : undefined,
              comment: comment || undefined,
              organization_id: selectedOrganizationId,
              event_id: selon === 'event' ? eventId : undefined,
            },
            file ? [file] : undefined
          );
          assigned += response.assigned_count || recipientIds.length;
          collectedProofs.push(...proofsFromAssignResponse(response));
        }
      }

      if (lastBadge) {
        writeAttestLastSeries(state.user?.id, projectId, {
          series,
          catalog_key: lastBadge.catalog_key ?? null,
          badge_series_id: lastBadge.badge_series_id ?? null,
        });
        setLastSeriesTag(series);
      }

      showSuccessToast(
        assigned > 1
          ? `${assigned} preuves de compétence produites — une par personne, portant les constats cochés.`
          : `Preuve de compétence produite pour « ${lastBadge ? getBadgeDisplayName(lastBadge.name) : 'la compétence'} ».`
      );

      const selectedParticipant = participants.find((p) => p.memberId === selectedParticipants[0]);
      if (selectedParticipant && lastBadge) {
        onAssign({
          id: `badge-${Date.now()}`,
          badgeId: lastBadge.id.toString(),
          badgeTitle: lastBadge.name,
          badgeSeries: lastBadge.series,
          badgeLevel: lastBadge.level.replace('level_', ''),
          badgeImage: lastBadge.image_url || '/TouKouLeur-Jaune.png',
          participantId: selectedParticipant.memberId,
          participantName: selectedParticipant.name,
          participantAvatar: selectedParticipant.avatar,
          participantOrganization: selectedParticipant.organization || 'Non spécifiée',
          attributedBy: state.user?.id?.toString() || '',
          attributedByName: state.user?.name || '',
          attributedByAvatar: state.user?.avatar || '',
          attributedByOrganization: state.user?.organization || 'Non spécifiée',
          projectId,
          projectTitle: projectTitle || '',
          domaineEngagement: '',
          commentaire: commentaire || undefined,
          dateAttribution: new Date().toISOString(),
        });
      }
      const itemLabels =
        lastBadge && pickedDraft
          ? getBadgeCompetencies(lastBadge)
              .filter((c) => pickedDraft.expertiseIds.includes(c.id))
              .map((c) => getCompetencyDisplayName(c.name || String(c.id), false))
          : [];
      const observedAtLabel = new Date().toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
      setSuccessShowAllProofs(false);
      setSuccessState({
        count: assigned,
        badgeName: lastBadge
          ? formatBadgeHeadingWithCode(getBadgeDisplayName(lastBadge.name))
          : 'la compétence',
        proofs: collectedProofs,
        participantNames: selectedParticipantNames,
        itemLabels,
        observedAtLabel,
      });
    } catch (error: any) {
      const apiMessage = error.response?.data?.message || error.response?.data?.error;
      showErrorToast(apiMessage || "Erreur lors de l'attestation");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <div className="badge-assignment-modal-overlay" onClick={requestClose}>
      <div className="att-modal" role="dialog" aria-modal="true" aria-labelledby="att-title" onClick={(e) => e.stopPropagation()}>
        <div className="att-topbar">
          <div className="att-logo">K</div>
          <h2 id="att-title" className="att-topbar-title">Attester une compétence</h2>
          <div className="att-topbar-org">
            <div className="att-org-name">{isIndividual ? preselectedPerson?.name || 'Participant' : selectedOrgName || 'Votre structure'}</div>
            <div className="att-org-project">
              {isIndividual ? preselectedPerson?.organization || projectTitle || 'Projet' : `projet « ${projectTitle || 'Projet'} »`}
            </div>
          </div>
          <button type="button" className="att-close" onClick={requestClose} aria-label="Fermer">×</button>
        </div>
        {!successState && (
        <div className="att-steps">
          {stepDefs.map((s, i) => (
            <React.Fragment key={s.key}>
              {i > 0 && <span className="att-step-sep">—</span>}
              <span className={`att-step ${i < currentStepIndex ? 'done' : i === currentStepIndex ? 'current' : ''}`}>
                <span className="num">{i + 1}</span> {s.label}
              </span>
            </React.Fragment>
          ))}
        </div>
        )}

        {!successState && currentStepIndex === 0 && organizationsForSelection.length > 1 && (
          <div className="att-org-picker">
            <div className="att-slab">L’organisation</div>
            <select
              className="att-org"
              value={selectedOrganizationId || ''}
              onChange={(e) => setSelectedOrganizationId(parseInt(e.target.value, 10))}
            >
              <option value="">Sélectionner une organisation</option>
              {organizationsForSelection.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="att-body">
          {successState && (
            <div className="att-success">
              <div className="att-success-icon">✓</div>
              <div className="att-success-title">
                {successState.count > 1
                  ? `${successState.count} preuves de compétence produites`
                  : 'Preuve de compétence produite'}
              </div>
              <p className="att-success-sub">
                {successState.count > 1
                  ? `Une par personne, portant les constats cochés pour « ${successState.badgeName} ».`
                  : `Pour « ${successState.badgeName} ».`}
              </p>
              <div className="att-success-hero">
                <div className="att-success-hero-card">
                  <div className="att-success-hero-kicker">Preuve Kinship · Compétence</div>
                  <div className="att-success-hero-title">{successState.badgeName}</div>
                  <div className="att-success-hero-meta">
                    {successState.participantNames[0] ||
                      successState.proofs[0]?.holderName ||
                      'Participant'}
                    {successState.observedAtLabel
                      ? ` · constaté le ${successState.observedAtLabel}`
                      : ''}
                  </div>
                  {successState.itemLabels.length > 0 && (
                    <ul className="att-success-hero-items">
                      {successState.itemLabels.map((label) => (
                        <li key={label}>{label}</li>
                      ))}
                    </ul>
                  )}
                </div>
                {successState.count > 1 && !successShowAllProofs && (
                  <button
                    type="button"
                    className="att-back-link att-success-more"
                    onClick={() => setSuccessShowAllProofs(true)}
                  >
                    Et {successState.count - 1} autre{successState.count - 1 > 1 ? 's' : ''} — voir
                    les {successState.count} preuves
                  </button>
                )}
                {successShowAllProofs && successState.proofs.length > 0 && (
                  <div className="att-success-proof">
                    {successState.proofs.map((proof, idx) => (
                      <div
                        key={proof.proofNumber || `proof-${idx}`}
                        style={{ marginBottom: idx < successState.proofs.length - 1 ? 12 : 0 }}
                      >
                        <ProofCardCompact proof={proof} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <p className="att-success-follow">
                Un autre constat ? Les mêmes participants restent sélectionnés.
              </p>
              <div className="att-success-actions att-success-actions-row">
                <button type="button" className="att-back-link" onClick={onClose}>
                  Terminer — retour au projet
                </button>
                <button type="button" className="att-success-btn" onClick={handleOtherCompetence}>
                  Autre compétence
                </button>
                {!isIndividual && (
                  <button
                    type="button"
                    className="att-go"
                    onClick={handleSameCompetenceOtherParticipants}
                  >
                    Même compétence, autres participants
                  </button>
                )}
              </div>
            </div>
          )}
          {!successState && step === 'serie' && (
            <>
              <div className="pt-2 att-qline">Dans quelle série ?</div>
              <p className="att-qhelp">Un constat, un ou plusieurs participants.</p>
              {loadingBadges && <p className="att-hint">Chargement…</p>}
              {(groupedSeries.own.length + groupedSeries.catalogue.length > 10 || seriesQuery) && (
                <input
                  className="att-search"
                  placeholder="Rechercher…"
                  value={seriesQuery}
                  onChange={(e) => setSeriesQuery(e.target.value)}
                />
              )}
              {groupedSeries.own.length > 0 && <div className="att-grp">Vos séries</div>}
              {groupedSeries.own.length > 0 && (
                <div className="att-axe-filters" role="list">
                  {groupedSeries.own.map((s) => (
                    <button
                      key={s}
                      type="button"
                      className={`att-axe-chip ${s === series ? 'on' : ''}`}
                      onClick={() => chooseSeries(s)}
                    >
                      {seriesLabel(s)}
                      {lastSeriesTag === s && (
                        <span className="att-pill-tag"> · dernière utilisée</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
              {/* Distribuées : groupe vide masqué (Patrick reprise 3) — series_owner BE */}
              {groupedSeries.catalogue.length > 0 && <div className="att-grp">Catalogue Kinship</div>}
              {groupedSeries.catalogue.length > 0 && (
                <div className="att-axe-filters" role="list">
                  {groupedSeries.catalogue.map((s) => (
                    <button
                      key={s}
                      type="button"
                      className={`att-axe-chip ${s === series ? 'on' : ''}`}
                      onClick={() => chooseSeries(s)}
                    >
                      {seriesLabel(s)}
                      {lastSeriesTag === s && (
                        <span className="att-pill-tag"> · dernière utilisée</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {!successState && step === 'axe' && wizardHasAxeStep && (
            <>
              <div className="att-grp">{seriesLabel(series)}</div>
              <div className="att-qline">Dans quel axe ?</div>
              <p className="att-qhelp">Plusieurs axes dans cette série.</p>
              {treeLoading && <p className="att-hint">Chargement de l’arbre…</p>}
              {treeAxeOptions.map((axe) => {
                const names = competenceNames.filter((n) => axe.badgeNames.includes(n) && !frozenNames.has(n));
                if (names.length === 0 && !treeLoading) return null;
                return (
                  <button
                    key={axe.id}
                    type="button"
                    className={`att-srow ${selectedAxe === axe.title ? 'sel' : ''}`}
                    onClick={() => {
                      setSelectedAxe(axe.title);
                      setDrafts({});
                      setStep('competence');
                    }}
                  >
                    {axe.title}
                    <span className="ct">
                      {names.length} compétence{names.length > 1 ? 's' : ''}
                    </span>
                  </button>
                );
              })}
              <div className="att-navbar">
                <button type="button" className="att-back-link" onClick={goBack}>
                  Retour
                </button>
                <span className="att-navhint">un clic sur une ligne passe à l’étape suivante</span>
              </div>
            </>
          )}

          {!successState && step === 'competence' && (
            <>
              {isIndividual && frozen.length > 0 && (
                <>
                  <div className="att-slab">
                    Déjà attesté <span className="n">— figé au geste</span>
                  </div>
                  {frozen.map((f) => (
                    <div key={`${f.name}-${f.level}`} className="att-frozen">
                      <span className="att-ok">✓</span>
                      {getBadgeDisplayName(f.name)}
                        <span className="att-lv">{getLevelLabel(badgesBySeries[series]?.[0]?.catalog_key || badgesBySeries[series]?.[0]?.series || series, f.level)}</span>
                      <span style={{ marginLeft: 'auto', fontSize: 11, color: '#8f8d86' }}>
                        {f.date ? `le ${f.date} · ` : ''}sa preuve est née
                      </span>
                    </div>
                  ))}
                </>
              )}
              <div className="att-grp">{seriesLabel(series)}</div>
              <div className="att-qline">Qu’avez-vous observé ?</div>
              <p className="att-qhelp">
                Un clic sur une pastille choisit la compétence et passe à l’étape suivante
                {treeAxeOptions.length > 0 ? ' — filtrez par axe si besoin' : ''}.
              </p>
              {treeAxeOptions.length > 0 && (
                <div className="att-axe-filters" role="group" aria-label="Filtrer par axe">
                  <button
                    type="button"
                    className={`att-axe-chip ${!selectedAxe ? 'on' : ''}`}
                    onClick={() => setSelectedAxe(null)}
                  >
                    Tous les axes
                  </button>
                  {treeAxeOptions.map((axe) => (
                    <button
                      key={axe.id}
                      type="button"
                      className={`att-axe-chip ${selectedAxe === axe.title ? 'on' : ''}`}
                      onClick={() => setSelectedAxe(axe.title)}
                    >
                      {axe.title}
                    </button>
                  ))}
                </div>
              )}
              {useParentCompetenceCards
                ? (() => {
                    const byAxe = new Map<string | null, typeof parentCompetenceCards>();
                    parentCompetenceCards.forEach((card) => {
                      const k = selectedAxe ? null : card.axeTitle;
                      const list = byAxe.get(k) || [];
                      list.push(card);
                      byAxe.set(k, list);
                    });
                    return Array.from(byAxe.entries()).map(([axeTitle, cards]) => (
                      <React.Fragment key={axeTitle || '__all__'}>
                        {axeTitle && <div className="att-grp">{axeTitle}</div>}
                        {cards.map((card) => {
                          const sampleName = card.badgeNames[0];
                          const cpsIcon = getCpsLocalBadgeImage(sampleName);
                          const phaseNum = phaseNumberFromCpsCompetenceCode(card.code);
                          const phaseHint = phaseNum
                            ? getLevelLabel(CATALOG_KEY_CPS, phaseNum)
                            : null;
                          const title = card.code
                            ? `${card.code} · ${card.name}`
                            : card.name;
                          const anyPicked = card.badgeNames.some((n) => Boolean(drafts[n]));
                          return (
                            <div
                              key={card.key}
                              className={
                                anyPicked
                                  ? 'att-card valid att-card-pastille'
                                  : 'att-card att-card-pastille'
                              }
                            >
                              <div className="att-crow" style={{ border: 0, padding: 0, width: '100%' }}>
                                <span className="att-avatar" aria-hidden="true">
                                  {cpsIcon ? (
                                    <img src={cpsIcon} alt="" width={28} height={28} />
                                  ) : (
                                    (card.code || card.name).trim().charAt(0)
                                  )}
                                </span>
                                <span className="att-card-meta">
                                  <span className="att-card-title">{title}</span>
                                  {phaseHint && (
                                    <span className="att-card-phase">{phaseHint}</span>
                                  )}
                                </span>
                              </div>
                              <div className="att-axe-filters att-level-pills" role="group">
                                {sortBadgeNamesBySpfCode(card.badgeNames).map((name) => {
                                  const levels = levelsFor(name);
                                  const lv = drafts[name]?.level || levels[0] || '1';
                                  const on = Boolean(drafts[name]);
                                  return (
                                    <button
                                      key={name}
                                      type="button"
                                      className={`att-axe-chip att-pastille-long ${on ? 'on' : ''}`}
                                      onClick={() => pickCompetenceAndAdvance(name, lv)}
                                    >
                                      {cpsPastilleLabelFromBadgeName(getBadgeDisplayName(name))}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </React.Fragment>
                    ));
                  })()
                : competenceGroups.map((group) => (
                    <React.Fragment key={group.title || '__all__'}>
                      {group.title && <div className="att-grp">{group.title}</div>}
                      {group.names.map((name) => {
                        const draft = drafts[name];
                        const picked = Boolean(draft);
                        const levels = levelsFor(name);
                        const sampleBadge = badgeFor(name, levels[0] || '1');
                        const cpsIcon = getCpsLocalBadgeImage(name);
                        const labelSeries =
                          sampleBadge?.catalog_key || sampleBadge?.series || series;
                        const phaseHint =
                          sampleBadge &&
                          (isCompetencesPsychosocialesSeries(sampleBadge.series) ||
                            sampleBadge.catalog_key === CATALOG_KEY_CPS)
                            ? getLevelLabel(labelSeries, levels[0] || '1')
                            : levels.length === 1
                              ? getLevelLabel(labelSeries, levels[0])
                              : null;
                        return (
                          <div
                            key={name}
                            className={
                              picked
                                ? 'att-card valid att-card-pastille'
                                : 'att-card att-card-pastille'
                            }
                          >
                            <div
                              className="att-crow"
                              style={{ border: 0, padding: 0, width: '100%' }}
                            >
                              <span className="att-avatar" aria-hidden="true">
                                {cpsIcon ? (
                                  <img src={cpsIcon} alt="" width={28} height={28} />
                                ) : (
                                  getBadgeDisplayName(name).trim().charAt(0)
                                )}
                              </span>
                              <span className="att-card-meta">
                                <span className="att-card-title">
                                  {getBadgeDisplayName(name)}
                                </span>
                                {phaseHint && (
                                  <span className="att-card-phase">{phaseHint}</span>
                                )}
                              </span>
                            </div>
                            <div className="att-axe-filters att-level-pills" role="group">
                              {levels.map((lv) => (
                                <button
                                  key={lv}
                                  type="button"
                                  className={`att-axe-chip ${draft?.level === lv ? 'on' : ''}`}
                                  onClick={() => pickCompetenceAndAdvance(name, lv)}
                                >
                                  {getLevelLabel(labelSeries, lv)}
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </React.Fragment>
                  ))}
              {!showAllCompetences &&
                treeAxeOptions.length === 0 &&
                competenceNames.filter((n) => !frozenNames.has(n)).length >
                  namesForCompetenceStep.length && (
                  <button
                    type="button"
                    className="att-dash"
                    onClick={() => setShowAllCompetences(true)}
                  >
                    ▸ toute la série (
                    {competenceNames.filter((n) => !frozenNames.has(n)).length})
                  </button>
                )}
              <div className="att-bottombar">
                <div className="att-recap">
                  <button type="button" className="att-back-link" onClick={goBack}>
                    Retour
                  </button>
                  <span className="att-navhint">un clic sur une pastille passe à l’étape suivante</span>
                </div>
              </div>
            </>
          )}

          {!successState && step === 'participants' && !isIndividual && (
            <>
              <div className="att-grp">
                {pickedName ? formatBadgeHeadingWithCode(getBadgeDisplayName(pickedName)) : ''}
              </div>
              <div className="att-qline">Qui avez-vous observé ?</div>
              <p className="att-qhelp">Sélectionner les participants.</p>
              <div className="att-slab-row">
                <div className="att-slab">
                  Participants du projet{' '}
                  <span className="n">— {attestParticipants.length}</span>
                </div>
                <button type="button" className="att-tout-cocher" onClick={selectAllParticipants}>
                  Tout sélectionner
                </button>
              </div>
              <input
                className="att-search"
                placeholder="Rechercher…"
                value={participantsSearchTerm}
                onChange={(e) => setParticipantsSearchTerm(e.target.value)}
              />
              <div className="att-participants-grid">
                {(participantsSearchTerm || participantsExpanded
                  ? filteredParticipants
                  : filteredParticipants.slice(0, PARTICIPANTS_INITIAL_COUNT)
                ).map((p) => {
                  const checked = selectedParticipants.includes(p.memberId);
                  return (
                    <button
                      key={p.memberId}
                      type="button"
                      className={`att-participant-row ${checked ? 'on' : ''}`}
                      onClick={() => toggleParticipant(p.memberId)}
                    >
                      <span className={`att-box ${checked ? 'on' : ''}`} />
                      {chipName(p.name)}
                    </button>
                  );
                })}
              </div>
              {!participantsSearchTerm && !participantsExpanded && filteredParticipants.length > PARTICIPANTS_INITIAL_COUNT && (
                <button
                  type="button"
                  className="att-participants-more"
                  onClick={() => setParticipantsExpanded(true)}
                >
                  {filteredParticipants.length - PARTICIPANTS_INITIAL_COUNT} autres participants…
                </button>
              )}
              <div className="att-bottombar">
                <div className="att-recap">
                  <button type="button" className="att-back-link" onClick={goBack}>
                    Retour
                  </button>
                  {selectedParticipants.length} participant{selectedParticipants.length > 1 ? 's' : ''} sélectionné
                  {selectedParticipants.length > 1 ? 's' : ''}
                </div>
                <button
                  type="button"
                  className="att-go"
                  disabled={selectedParticipants.length === 0}
                  onClick={() => setStep('constat')}
                >
                  Continuer
                </button>
              </div>
            </>
          )}

          {!successState && step === 'constat' && pickedName && pickedDraft && (
            <>
              <div className="att-grp">
                {formatBadgeHeadingWithCode(getBadgeDisplayName(pickedName))} ·{' '}
                {levelLabelFor(pickedDraft.level, pickedName)}
                {pickedComps.length > 0
                  ? ` · ${pickedDraft.expertiseIds.length}/${pickedComps.length} coché${pickedDraft.expertiseIds.length > 1 ? 's' : ''}`
                  : ''}
              </div>

              {pickedBadge &&
                isIndividual &&
                (commentRequiredFor(pickedBadge) || documentRequiredFor(pickedBadge)) && (
                  <div className="att-doctrine">
                    {commentRequiredFor(pickedBadge)
                      ? `Commentaire obligatoire ✱${pickedBadge.level === 'level_2' ? ' · document fortement conseillé' : documentRequiredFor(pickedBadge) ? ' · document obligatoire' : ''}`
                      : 'Commentaire et document optionnels'}
                  </div>
                )}

              {pickedBadge && !isIndividual && (massNeedsComment || massNeedsDoc) && (
                <div className="att-doctrine">
                  {`Commentaire obligatoire ✱${massNeedsDoc ? ' · document obligatoire' : massRecommendsDoc ? ' · document fortement conseillé' : ''}`}
                </div>
              )}

              {isIndividual ? (
                <>
                  {pickedComps.length > 0 && pickedBadge ? (
                    <ChecklistItems
                      comps={pickedComps}
                      checkedIds={pickedDraft.expertiseIds}
                      series={pickedBadge.catalog_key || pickedBadge.series}
                      onToggle={(id) =>
                        toggleExpertise(
                          pickedName,
                          id,
                          isSingleSelectCompetenceSeries(pickedBadge.series, pickedBadge.catalog_key)
                        )
                      }
                      onToggleAll={() =>
                        toggleAllExpertise(
                          pickedName,
                          pickedComps.map((c) => c.id),
                          pickedDraft.expertiseIds
                        )
                      }
                    />
                  ) : (
                    <p className="att-hint">Rien à cocher pour cette compétence — la sélection suffit.</p>
                  )}
                </>
              ) : (
                <>
                  <div className="att-slab-row">
                    <div className="att-slab">
                      Les mêmes constats pour <span className="n">{joinFr(selectedParticipantNames)}</span>
                    </div>
                    {constatMode === 'groupe' && selectedParticipants.length > 1 && (
                      <button type="button" className="att-tout-cocher" onClick={enterIndividualMode}>
                        Ajuster participant par participant
                      </button>
                    )}
                  </div>

                  {constatMode === 'individuel' ? (
                    <>
                      <div className="att-slab-row">
                        <div className="att-slab">Constat propre à chaque participant</div>
                        <button type="button" className="att-tout-cocher" onClick={exitIndividualMode}>
                          Revenir au mode groupe
                        </button>
                      </div>
                      <div className="att-participant-switch">
                        {selectedParticipants.map((pid) => {
                          const p = participants.find((pp) => pp.memberId === pid);
                          const d = individualDrafts[pid];
                          const active = activeParticipantId === pid;
                          return (
                            <button
                              key={pid}
                              type="button"
                              className={`att-switch-chip ${active ? 'on' : ''}`}
                              onClick={() => setActiveParticipantId(pid)}
                            >
                              {p ? chipName(p.name) : pid}
                              {d && pickedComps.length > 0 ? ` · ${d.expertiseIds.length}/${pickedComps.length}` : ''}
                            </button>
                          );
                        })}
                      </div>

                      {activeParticipantId && individualDrafts[activeParticipantId] && (() => {
                        const aid = activeParticipantId;
                        const ad = individualDrafts[aid];
                        const aName = participants.find((p) => p.memberId === aid)?.name;
                        return (
                          <>
                            {pickedComps.length > 0 ? (
                              <ChecklistItems
                                comps={pickedComps}
                                checkedIds={ad.expertiseIds}
                                series={pickedBadge!.series}
                                onToggle={(id) =>
                                  toggleIndividualExpertise(aid, id, isSingleSelectCompetenceSeries(pickedBadge!.series, pickedBadge!.catalog_key))
                                }
                                onToggleAll={() =>
                                  toggleAllIndividualExpertise(aid, pickedComps.map((c) => c.id), ad.expertiseIds)
                                }
                                hint={`Un seul item coché suffit à produire une preuve — propre à ${aName ? chipName(aName) : 'ce participant'}.`}
                              />
                            ) : (
                              <p className="att-hint">Rien à cocher pour cette compétence — la sélection suffit.</p>
                            )}
                            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                              <input
                                className="att-search"
                                style={{ margin: 0, flex: 1, minWidth: 140 }}
                                placeholder={pickedBadge && commentRequiredFor(pickedBadge) ? 'Commentaire ✱' : 'Commentaire (optionnel)'}
                                value={ad.comment}
                                onChange={(e) =>
                                  setIndividualDrafts((prev) => ({
                                    ...prev,
                                    [aid]: { ...prev[aid], comment: e.target.value },
                                  }))
                                }
                              />
                              <label className="att-attach">
                                {ad.fileName || 'Joindre un document'}
                                <input
                                  type="file"
                                  hidden
                                  accept=".pdf,.jpg,.jpeg,.png,.mp4,.mov,.doc,.docx,.mp3"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    setIndividualDrafts((prev) => ({
                                      ...prev,
                                      [aid]: { ...prev[aid], file, fileName: file.name },
                                    }));
                                  }}
                                />
                              </label>
                            </div>
                          </>
                        );
                      })()}
                    </>
                  ) : pickedComps.length > 0 ? (
                    <ChecklistItems
                      comps={pickedComps}
                      checkedIds={pickedDraft.expertiseIds}
                      series={pickedBadge!.series}
                      onToggle={(id) => toggleExpertise(pickedName, id, isSingleSelectCompetenceSeries(pickedBadge!.series, pickedBadge!.catalog_key))}
                      onToggleAll={() =>
                        toggleAllExpertise(pickedName, pickedComps.map((c) => c.id), pickedDraft.expertiseIds)
                      }
                    />
                  ) : (
                    <p className="att-hint">Rien à cocher pour cette compétence — la sélection suffit.</p>
                  )}
                </>
              )}

              {projectEvents.length > 0 && (
                <div className="att-selon">
                  <b>Constaté pendant :</b>
                  <button type="button" className="att-tog on" onClick={() => setEventOpen((v) => !v)}>
                    {selon === 'event' && eventId ? projectEvents.find((e) => e.id === eventId)?.title || 'un événement' : 'Le projet'} ▾
                  </button>
                </div>
              )}
              {projectEvents.length > 0 && eventOpen && (
                <div className="att-ev-list">
                  <button
                    type="button"
                    className={`att-srow ${selon === 'project' ? 'sel' : ''}`}
                    onClick={() => {
                      setSelon('project');
                      setEventId(undefined);
                      setEventOpen(false);
                    }}
                  >
                    Le projet
                  </button>
                  {projectEvents.map((ev) => (
                    <button
                      key={ev.id}
                      type="button"
                      className={`att-srow ${selon === 'event' && eventId === ev.id ? 'sel' : ''}`}
                      onClick={() => {
                        setSelon('event');
                        setEventId(ev.id);
                        setEventOpen(false);
                      }}
                    >
                      {ev.title}
                    </button>
                  ))}
                </div>
              )}

              {(() => {
                const needsDoc = isIndividual
                  ? Boolean(pickedBadge && documentRequiredFor(pickedBadge))
                  : massNeedsDoc;
                const needsComment = isIndividual
                  ? Boolean(pickedBadge && commentRequiredFor(pickedBadge))
                  : massNeedsComment;
                const showFacultatifTitle = !needsDoc && !needsComment;
                const fileLabel = isIndividual
                  ? pickedDraft?.fileName || 'Joindre un document'
                  : fileName || 'Joindre un document';
                const commentValue = isIndividual ? pickedDraft?.comment || '' : commentaire;
                return (
                  <>
                    {showFacultatifTitle && (
                      <div className="att-facultatif-title">Facultatif — rien n’est exigé</div>
                    )}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                      {!needsDoc && !proofFieldOpen && (
                        <button
                          type="button"
                          className="att-attach"
                          onClick={() => setProofFieldOpen(true)}
                        >
                          + Preuve ou objet
                        </button>
                      )}
                      {(needsDoc || proofFieldOpen) && (
                        <label className="att-attach">
                          {fileLabel}
                          <input
                            type="file"
                            hidden
                            accept=".pdf,.jpg,.jpeg,.png,.mp4,.mov,.doc,.docx,.mp3"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              if (isIndividual && pickedName) {
                                setDrafts((prev) => ({
                                  ...prev,
                                  [pickedName]: {
                                    ...prev[pickedName],
                                    file,
                                    fileName: file.name,
                                  },
                                }));
                              } else {
                                setFichier(file);
                                setFileName(file.name);
                              }
                            }}
                          />
                        </label>
                      )}
                      {!needsComment && !commentFieldOpen && (
                        <button
                          type="button"
                          className="att-attach"
                          onClick={() => setCommentFieldOpen(true)}
                        >
                          + Commentaire
                        </button>
                      )}
                    </div>
                    {(needsComment || commentFieldOpen) && (
                      <textarea
                        className="att-comment"
                        style={{ marginTop: 8 }}
                        placeholder={
                          isIndividual
                            ? needsComment
                              ? 'Commentaire ✱'
                              : 'Commentaire (optionnel)'
                            : 'Il vaut pour tous les participants cochés — il justifie l’attestation commune.'
                        }
                        value={commentValue}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (isIndividual && pickedName) {
                            setDrafts((prev) => ({
                              ...prev,
                              [pickedName]: { ...prev[pickedName], comment: v },
                            }));
                          } else {
                            setCommentaire(v);
                          }
                        }}
                      />
                    )}
                  </>
                );
              })()}

              <div className="att-bottombar">
                <div className="att-recap">
                  <button type="button" className="att-back-link" onClick={goBack}>
                    Retour
                  </button>
                  {isIndividual ? (
                    <>
                      <b>Un geste — une preuve de compétence</b>
                      {proofCount > 0 ? ` — ici : ${proofCount}.` : '.'}
                    </>
                  ) : constatMode === 'individuel' ? (
                    <>
                      {selectedParticipants.length} participant
                      {selectedParticipants.length > 1 ? 's' : ''} —{' '}
                      {proofCount} preuve{proofCount > 1 ? 's' : ''} de compétence, chacune avec
                      ses propres constats.
                    </>
                  ) : (
                    <>
                      {selectedParticipants.length} participant
                      {selectedParticipants.length > 1 ? 's' : ''} —{' '}
                      {proofCount} preuve{proofCount > 1 ? 's' : ''} de compétence
                      {pickedDraft.expertiseIds.length > 0
                        ? `, chacune portant ${pickedDraft.expertiseIds.length > 1 ? 'les' : 'le'} ${pickedDraft.expertiseIds.length} constat${pickedDraft.expertiseIds.length > 1 ? 's' : ''}.`
                        : '.'}
                    </>
                  )}
                </div>
                <button
                  type="button"
                  className={`att-go ${attestArmed ? 'att-go-confirm' : ''}`}
                  disabled={submitting || !canAttest}
                  onClick={() => {
                    if (!canAttest) return;
                    if (!attestArmed) {
                      setAttestArmed(true);
                      return;
                    }
                    void handleSubmit();
                  }}
                >
                  {submitting
                    ? 'Attestation…'
                    : attestArmed
                      ? isIndividual
                        ? 'Confirmer · c’est définitif'
                        : `Confirmer — ${selectedParticipants.length} participant${selectedParticipants.length > 1 ? 's' : ''} · c’est définitif`
                      : isIndividual
                        ? 'Attester'
                        : `Attester — ${selectedParticipants.length} participant${selectedParticipants.length > 1 ? 's' : ''}`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>

    {closeConfirmOpen && (
      <div className="att-confirm-overlay" onClick={() => setCloseConfirmOpen(false)}>
        <div className="att-confirm-card" onClick={(e) => e.stopPropagation()}>
          <h3 className="att-confirm-title">Quitter sans attester ?</h3>
          <p className="att-confirm-sub">
            Ce qui a été coché dans ce geste sera perdu — série, compétence, participants et constats.
          </p>
          <div className="att-confirm-actions">
            <button type="button" className="att-confirm-cancel" onClick={() => setCloseConfirmOpen(false)}>
              Continuer le geste
            </button>
            <button
              type="button"
              className="att-go att-danger"
              onClick={() => {
                setCloseConfirmOpen(false);
                onClose();
              }}
            >
              Quitter
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default AttestCompetenceModal;
